import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { hasCapability, isAdminRole, formatDateTime } from "@/lib/permissions";
import { ArrowLeft, CheckCircle2, XCircle, Truck, Download, FileText, PackageCheck, Ban, User, Calendar, ArrowRightLeft, LogIn, ShieldCheck } from "lucide-react";

const STATUS = {
  issued: { icon: Truck, cls: "bg-blue-50 text-blue-800 border-blue-200", label: "In Transit" },
  partially_received: { icon: PackageCheck, cls: "bg-teal-50 text-teal-800 border-teal-200", label: "Partially Received" },
  received: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Received in Full" },
  rejected: { icon: XCircle, cls: "bg-rose-50 text-rose-800 border-rose-200", label: "Rejected" },
  cancelled: { icon: Ban, cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Cancelled" },
};

export default function MaterialTransferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // 'receive' | 'reject' | 'gate-in'
  const [receiveNote, setReceiveNote] = useState("");
  const [receiveQty, setReceiveQty] = useState({});
  const [rejectReason, setRejectReason] = useState("");
  const [gateForm, setGateForm] = useState({ gate_number: "", vehicle_number: "", driver_name: "", notes: "" });

  const canReceive = hasCapability(user, "receive_material_transfer");
  const canGateIn = hasCapability(user, "gate_in_material_transfer");
  const isIssuer = doc?.issued_by === user?.id;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/material-transfers/${id}`);
      setDoc(data);
      // Pre-fill receive qty with the "remaining" per line
      const rq = {};
      (data.items || []).forEach((it) => {
        rq[it.id] = String((Number(it.qty) || 0));
      });
      setReceiveQty(rq);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load transfer");
      navigate("/material-transfers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doReceive = async () => {
    setBusy(true);
    try {
      const map = {};
      Object.entries(receiveQty).forEach(([k, v]) => {
        const n = Number(v);
        if (!Number.isNaN(n) && n >= 0) map[k] = n;
      });
      await api.post(`/material-transfers/${id}/receive`, { received_qty_by_item: map, notes: receiveNote });
      toast.success("Transfer marked as received");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to mark received");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return toast.error("Please provide a rejection reason");
    setBusy(true);
    try {
      await api.post(`/material-transfers/${id}/reject`, { reason: rejectReason });
      toast.success("Transfer rejected");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to reject");
    } finally { setBusy(false); }
  };

  const doGateIn = async () => {
    if (!gateForm.gate_number.trim()) return toast.error("Gate number is required");
    setBusy(true);
    try {
      await api.post(`/material-transfers/${id}/gate-in`, gateForm);
      toast.success("Gate-in recorded. Receiver can now open and verify the shipment.");
      setDialog(null);
      setGateForm({ gate_number: "", vehicle_number: "", driver_name: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to record gate-in");
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel this transfer? Cannot be undone.")) return;
    setBusy(true);
    try {
      await api.post(`/material-transfers/${id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to cancel");
    } finally { setBusy(false); }
  };

  const downloadPdf = async (kind) => {
    const token = localStorage.getItem("assetiq_token");
    const res = await fetch(`${API_BASE}/material-transfers/${id}/${kind}.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Failed to download PDF");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `${doc.transfer_number.replace(/\//g, "-")}_${kind}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  if (loading || !doc) return <div className="p-12 text-center text-slate-400">Loading…</div>;
  const st = STATUS[doc.status] || STATUS.issued;
  const StIcon = st.icon;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="transfer-detail-page">
      <button onClick={() => navigate("/material-transfers")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A] flex items-center gap-1"><ArrowRightLeft className="w-3 h-3" /> {doc.from_location} → {doc.to_location}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 font-mono">{doc.transfer_number}</h1>
          <div className="mt-2">
            <Badge className={`gap-1 border ${st.cls}`} data-testid="transfer-status-badge">
              <StIcon className="w-3.5 h-3.5" /> {st.label}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => downloadPdf("transfer-note")} data-testid="dl-transfer-note-btn">
            <FileText className="w-4 h-4 mr-2" /> Transfer Note PDF
          </Button>
          {(doc.status === "received" || doc.status === "partially_received" || doc.status === "rejected") && (
            <Button variant="outline" onClick={() => downloadPdf("receipt-note")} data-testid="dl-receipt-note-btn">
              <Download className="w-4 h-4 mr-2" /> Receipt Note PDF
            </Button>
          )}
          {(doc.status === "issued" || doc.status === "partially_received") && !doc.gate_in_status && canGateIn && (
            <Button onClick={() => setDialog("gate-in")} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="gate-in-btn">
              <LogIn className="w-4 h-4 mr-2" /> Record Gate-In
            </Button>
          )}
          {(doc.status === "issued" || doc.status === "partially_received") && canReceive && (
            <Button
              onClick={() => setDialog("receive")}
              disabled={!doc.gate_in_status}
              title={!doc.gate_in_status ? "Gate-in must be recorded first" : undefined}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:hover:bg-slate-300 disabled:cursor-not-allowed"
              data-testid="mark-received-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Received
            </Button>
          )}
          {doc.status === "issued" && canReceive && (
            <Button variant="outline" onClick={() => setDialog("reject")} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="reject-btn">
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </Button>
          )}
          {doc.status === "issued" && (isIssuer || isAdminRole(user?.role)) && (
            <Button variant="outline" onClick={doCancel} disabled={busy} className="text-slate-500">
              <Ban className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {doc.rejection_reason && (
        <Card className="bg-rose-50 border-rose-200">
          <CardContent className="p-4 flex gap-2">
            <XCircle className="w-5 h-5 text-rose-600 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-rose-800">Rejected by {doc.received_by_name}</div>
              <div className="text-sm text-rose-700 mt-1">{doc.rejection_reason}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2-step receiving progress */}
      {(doc.status === "issued" || doc.status === "partially_received" || doc.gate_in_status) && (
        <Card className={doc.gate_in_status ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"} data-testid="gate-in-panel">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${doc.gate_in_status ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {doc.gate_in_status ? <ShieldCheck className="w-5 h-5" /> : <LogIn className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">
                  {doc.gate_in_status ? "Gate-In Recorded" : "Awaiting Physical Gate-In"}
                </div>
                {doc.gate_in_status ? (
                  <div className="mt-1 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-0.5">
                    <span data-testid="gate-in-gate-number"><span className="text-slate-400">Gate:</span> <span className="font-mono font-semibold text-slate-800">{doc.gate_number}</span></span>
                    {doc.gate_vehicle_number && <span><span className="text-slate-400">Vehicle:</span> <span className="font-mono">{doc.gate_vehicle_number}</span></span>}
                    {doc.gate_driver_name && <span><span className="text-slate-400">Driver:</span> {doc.gate_driver_name}</span>}
                    <span><span className="text-slate-400">By:</span> {doc.gate_in_by_name} · {doc.gate_in_at ? formatDateTime(doc.gate_in_at) : ""}</span>
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-slate-600">
                    Shipment must be physically gated-in at the receiving location before the receiver can verify contents.
                    {canGateIn && <> Click <span className="font-semibold">Record Gate-In</span> to log the vehicle and gate number.</>}
                  </div>
                )}
                {doc.gate_in_notes && (
                  <div className="mt-1 text-xs text-slate-500 italic">Note: {doc.gate_in_notes}</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Meta icon={<Calendar className="w-3 h-3" />} label="Transfer Date" value={doc.transfer_date} />
        <Meta icon={<Calendar className="w-3 h-3" />} label="Expected By" value={doc.expected_delivery_by || "—"} />
        <Meta icon={<User className="w-3 h-3" />} label="Issued By" value={doc.issued_by_name} sub={doc.issued_by_email} />
        <Meta icon={<User className="w-3 h-3" />} label="Received By" value={doc.received_by_name || "—"} sub={doc.received_at ? formatDateTime(doc.received_at) : ""} />
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Purpose</div>
          <div className="text-sm text-slate-800">{doc.purpose}</div>
          {doc.notes && <><div className="text-[10px] uppercase text-slate-500 font-semibold mt-3 mb-1">Notes</div><div className="text-sm text-slate-800">{doc.notes}</div></>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-slate-500 font-semibold text-[11px] uppercase">
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-left">IM Code</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-left">UoM</th>
                  <th className="px-4 py-2 text-right">Received</th>
                  <th className="px-4 py-2 text-right">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(doc.items || []).map((it, i) => {
                  const done = (Number(it.qty_received) || 0) >= (Number(it.qty) || 0);
                  return (
                    <tr key={it.id}>
                      <td className="px-4 py-2 text-xs">{i + 1}</td>
                      <td className="px-4 py-2">
                        <div>{it.description}</div>
                        {it.notes && <div className="text-[11px] text-slate-500">{it.notes}</div>}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{it.im_code || "—"}</td>
                      <td className="px-4 py-2 text-right font-mono">{it.qty}</td>
                      <td className="px-4 py-2 text-xs">{it.uom}</td>
                      <td className={`px-4 py-2 text-right font-mono ${done ? "text-emerald-700 font-semibold" : Number(it.qty_received) > 0 ? "text-teal-700" : "text-slate-400"}`}>
                        {it.qty_received || 0}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-slate-500">{it.value ? `₹${it.value}` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Gate-In dialog */}
      <Dialog open={dialog === "gate-in"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><LogIn className="w-4 h-4 text-[#7C1D3A]" /> Record Physical Gate-In</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Log the vehicle arrival at the gate. The shipment cannot be marked received until this step is done.
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Gate Number *</label>
              <select
                autoFocus
                value={gateForm.gate_number}
                onChange={(e) => setGateForm({ ...gateForm, gate_number: e.target.value })}
                data-testid="gate-in-gate-number-input"
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C1D3A]/30 focus:border-[#7C1D3A]"
              >
                <option value="">Select gate…</option>
                {(doc?.direction === "office_to_factory"
                  ? ["Gate F1", "Gate F2A", "Gate F2B", "Gate F3A", "Gate F3B"]
                  : ["Main Gate"]
                ).map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400 mt-1">
                {doc?.direction === "office_to_factory"
                  ? "Factory gates F1 – F3B (Office → Factory)"
                  : "Main Gate at Head Office (Factory → Office)"}
              </p>
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Vehicle Number</label>
              <Input
                placeholder="e.g. MH01AB1234"
                value={gateForm.vehicle_number}
                onChange={(e) => setGateForm({ ...gateForm, vehicle_number: e.target.value })}
                data-testid="gate-in-vehicle-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Driver Name</label>
              <Input
                placeholder="Driver name (optional)"
                value={gateForm.driver_name}
                onChange={(e) => setGateForm({ ...gateForm, driver_name: e.target.value })}
                data-testid="gate-in-driver-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Notes</label>
              <Textarea
                rows={2}
                placeholder="Optional remarks"
                value={gateForm.notes}
                onChange={(e) => setGateForm({ ...gateForm, notes: e.target.value })}
                data-testid="gate-in-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              onClick={doGateIn}
              disabled={busy || !gateForm.gate_number.trim()}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="confirm-gate-in-btn"
            >
              {busy ? "Recording…" : "Record Gate-In"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive dialog */}
      <Dialog open={dialog === "receive"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Mark as Received</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">Enter the received qty per line. Defaults to full ordered qty.</div>
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded p-2 space-y-1">
              {(doc.items || []).map((it) => (
                <div key={it.id} className="grid grid-cols-12 gap-2 items-center text-sm">
                  <div className="col-span-8 truncate">{it.description}</div>
                  <div className="col-span-2 text-right text-slate-500 font-mono text-xs">of {it.qty} {it.uom}</div>
                  <div className="col-span-2">
                    <Input type="number" min="0" step="0.01" max={it.qty}
                      value={receiveQty[it.id] || ""}
                      onChange={(e) => setReceiveQty({ ...receiveQty, [it.id]: e.target.value })}
                      className="h-8 text-right text-sm" data-testid={`recv-qty-${it.id}`} />
                  </div>
                </div>
              ))}
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Notes</label>
              <Textarea rows={2} value={receiveNote} onChange={(e) => setReceiveNote(e.target.value)} placeholder="Optional receipt remarks" data-testid="receive-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={doReceive} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-receive-btn">
              {busy ? "Working..." : "Confirm receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={dialog === "reject"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Transfer</DialogTitle></DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4} placeholder="Reason for rejection (required)" data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={doReject} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject-btn">
              {busy ? "Working..." : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ icon, label, value, sub }) {
  return (
    <Card><CardContent className="p-3">
      <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1 tracking-wider">{icon}{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-1 truncate">{value || "—"}</div>
      {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
    </CardContent></Card>
  );
}
